import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function StockAdjustments() {
  const navigate = useNavigate();
  
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [adjustment, setAdjustment] = useState({
    product_id: '',
    warehouse_id: '',
    quantity_change: '',
    movement_type: 'adjustment',
    reason: '',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [movementsRes, productsRes, warehousesRes] = await Promise.all([
        apiFetch('/api/stock-adjustments'),
        apiFetch('/api/products'),
        apiFetch('/api/warehouses')
      ]);
      
      const [movementsData, productsData, warehousesData] = await Promise.all([
        movementsRes.json(),
        productsRes.json(),
        warehousesRes.json()
      ]);
      
      setMovements(movementsData);
      setProducts(productsData.filter(p => p.type === 'Product'));
      setWarehouses(warehousesData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
    }
  };

  const handleSubmitAdjustment = async (e) => {
    e.preventDefault();
    
    try {
      const res = await apiFetch('/api/stock-adjustments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...adjustment,
          quantity_change: parseFloat(adjustment.quantity_change)
        })
      });
      
      if (res.ok) {
        alert('Stock adjusted successfully!');
        setShowAdjustModal(false);
        setAdjustment({
          product_id: '',
          warehouse_id: '',
          quantity_change: '',
          movement_type: 'adjustment',
          reason: '',
          reference_number: '',
          notes: ''
        });
        fetchData();
      } else {
        alert('Failed to adjust stock');
      }
    } catch (err) {
      console.error('Error adjusting stock:', err);
      alert('Error adjusting stock');
    }
  };

  const filteredMovements = movements.filter(m =>
    m.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.warehouse_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Stock Adjustments"
        subtitle={`${movements.length} movements recorded`}
        backButton
        onBack={() => navigate('/inventory-landing')}
        backTitle="Back to Inventory"
        sticky
        actionButton={
          <button
            onClick={() => setShowAdjustModal(true)}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Adjust Stock
          </button>
        }
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Search */}
        <div className="mb-6 flex gap-4">
          <input
            type="text"
            placeholder="Search movements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Movements Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400">Loading movements...</div>
          </div>
        ) : filteredMovements.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No stock movements</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search' : 'Start by adjusting stock levels'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMovements.map((movement, index) => (
                    <tr key={movement.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(movement.movement_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{movement.product_name}</div>
                        <div className="text-xs text-gray-500 font-mono">{movement.product_code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{movement.warehouse_name}</div>
                        <div className="text-xs text-gray-500 font-mono">{movement.warehouse_code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-semibold ${
                          parseFloat(movement.quantity_change) > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {parseFloat(movement.quantity_change) > 0 ? '+' : ''}{parseFloat(movement.quantity_change).toFixed(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {movement.movement_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {movement.reason || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                        {movement.reference_number || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {movement.adjusted_by_name || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Adjust Stock Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">Adjust Stock</h2>
            
            <form onSubmit={handleSubmitAdjustment}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                    <select
                      value={adjustment.product_id}
                      onChange={(e) => setAdjustment({...adjustment, product_id: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select product...</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.product_code} - {p.product_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse *</label>
                    <select
                      value={adjustment.warehouse_id}
                      onChange={(e) => setAdjustment({...adjustment, warehouse_id: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select warehouse...</option>
                      {warehouses.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Change *</label>
                    <input
                      type="number"
                      step="0.1"
                      value={adjustment.quantity_change}
                      onChange={(e) => setAdjustment({...adjustment, quantity_change: e.target.value})}
                      required
                      placeholder="Use negative for removal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Positive to add, negative to remove</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={adjustment.movement_type}
                      onChange={(e) => setAdjustment({...adjustment, movement_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="adjustment">Adjustment</option>
                      <option value="count">Physical Count</option>
                      <option value="damage">Damaged</option>
                      <option value="loss">Lost/Stolen</option>
                      <option value="found">Found</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                  <input
                    type="text"
                    value={adjustment.reason}
                    onChange={(e) => setAdjustment({...adjustment, reason: e.target.value})}
                    required
                    placeholder="e.g., Physical count correction, Damaged in transit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={adjustment.reference_number}
                    onChange={(e) => setAdjustment({...adjustment, reference_number: e.target.value})}
                    placeholder="Optional job number, PO number, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={adjustment.notes}
                    onChange={(e) => setAdjustment({...adjustment, notes: e.target.value})}
                    rows={3}
                    placeholder="Additional details..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  Adjust Stock
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockAdjustments;
